import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trophy, Users, ClipboardList, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LiveNow } from "@/components/app/LiveNow";
import { TopRachas } from "@/components/app/TopRachas";

export default function CoordinatorDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/club");
        setData(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const kpis = data?.kpis || {};
  const chartData = (data?.categorias || []).map((c) => ({
    name: c.category.name.split("(")[0].trim(),
    "Goles a favor": c.gols_favor,
    "Goles en contra": c.gols_contra,
  }));

  return (
    <div>
      <div className="mb-8">
        <div className="overline">Vista global · Coordinación deportiva</div>
        <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter uppercase mt-2">
          Panel del <span className="text-primary">club</span>
        </h1>
      </div>
      <LiveNow />
      <TopRachas />

      <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 mb-8">
        <Kpi label="Categorías" value={kpis.categorias} />
        <Kpi label="Equipos" value={kpis.equipos} />
        <Kpi label="Jugadores" value={kpis.jugadores} />
        <Kpi label="Entrenadores" value={kpis.entrenadores} />
        <Kpi label="Partidos" value={kpis.partidos} />
        <Kpi label="Entrenamientos" value={kpis.entrenos} />
        <Kpi label="Jornadas totales" value={kpis.jornadas} />
        <div className="p-6 flex items-center justify-center">
          <div className="overline text-primary">Temporada activa</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <QuickCard title="Equipos" desc="Explora las plantillas y jugadores de cada categoría." to="/coordinator/teams" tid="quick-teams" />
        <QuickCard title="Staff Técnico" desc="Consulta los coordinadores, entrenadores y delegados del club." to="/club/staff" tid="quick-staff" />
        <QuickCard title="Evaluaciones" desc="Revisa las evaluaciones individuales de los jugadores." to="/evaluations" tid="quick-evaluations" />
        <QuickCard title="Comunicación" desc="Envía anuncios y convocatorias al club o al staff." to="/comms" tid="quick-comms" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border">
          <div className="p-4 border-b border-border">
            <div className="overline">Rendimiento ofensivo/defensivo por categoría</div>
          </div>
          <div className="p-4 h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }} />
                  <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="Goles a favor" fill="#0055FF" />
                  <Bar dataKey="Goles en contra" fill="#FF3B30" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin datos aún.</div>
            )}
          </div>
        </div>

        <div className="border border-border">
          <div className="p-4 border-b border-border">
            <div className="overline">Categorías</div>
          </div>
          <div className="divide-y divide-border">
            {(data?.categorias || []).map((c) => (
              <Link key={c.category.id} to={`/coordinator/teams?cat=${c.category.id}`} data-testid={`cat-summary-${c.category.id}`} className="flex items-center gap-3 p-4 hover:bg-white/[0.02] transition-colors group">
                <Trophy size={16} className="text-primary" />
                <div className="flex-1">
                  <div className="font-heading font-bold uppercase tracking-tight text-sm">{c.category.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {c.teams_count} equipos · {c.players_count} jugadores · {c.jornadas_count} jornadas
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="flex items-center gap-1 text-primary"><TrendingUp size={12} />{c.gols_favor}</span>
                  <span className="flex items-center gap-1 text-destructive"><TrendingDown size={12} />{c.gols_contra}</span>
                </div>
                <ArrowUpRight size={14} className="opacity-40 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="p-6">
      <div className="overline">{label}</div>
      <div className="metric-number text-5xl mt-3">{value ?? 0}</div>
    </div>
  );
}

function QuickCard({ title, desc, to, tid }) {
  return (
    <Link to={to} data-testid={tid} className="card-tactical p-5 flex items-start justify-between group">
      <div>
        <div className="font-heading font-bold uppercase tracking-tight text-lg">{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{desc}</div>
      </div>
      <ArrowUpRight size={18} className="text-primary opacity-60 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}
